import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { fetchCategories, fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import Seo from "../components/Seo";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "name", label: "Name A–Z" },
];

function Filters({ categories, params, update }) {
  return (
    <div className="space-y-8" data-testid="shop-filters">
      <div>
        <h4 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Category</h4>
        <ul className="space-y-2">
          <li>
            <button
              onClick={() => update({ category: null })}
              data-testid="filter-category-all"
              className={`text-sm transition-colors hover:text-brand-red ${!params.category ? "font-bold text-brand-red" : "text-neutral-600"}`}
            >
              All Products
            </button>
          </li>
          {categories.map((c) => (
            <li key={c.category_id}>
              <button
                onClick={() => update({ category: c.slug })}
                data-testid={`filter-category-${c.slug}`}
                className={`text-sm transition-colors hover:text-brand-red ${params.category === c.slug ? "font-bold text-brand-red" : "text-neutral-600"}`}
              >
                {c.name} <span className="text-neutral-400">({c.product_count})</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Price</h4>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            placeholder="Min"
            defaultValue={params.min || ""}
            onBlur={(e) => update({ min: e.target.value || null })}
            className="w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand-ink"
            aria-label="Minimum price"
            data-testid="filter-price-min"
          />
          <span className="text-neutral-400">–</span>
          <input
            type="number"
            min="0"
            placeholder="Max"
            defaultValue={params.max || ""}
            onBlur={(e) => update({ max: e.target.value || null })}
            className="w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand-ink"
            aria-label="Maximum price"
            data-testid="filter-price-max"
          />
        </div>
      </div>
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Availability</h4>
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-neutral-600">
          <input
            type="checkbox"
            checked={!!params.sale}
            onChange={(e) => update({ sale: e.target.checked || null })}
            className="h-4 w-4 accent-[#E60000]"
            data-testid="filter-on-sale"
          />
          On sale
        </label>
      </div>
    </div>
  );
}

export default function Shop() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);

  const params = useMemo(
    () => ({
      category: slug || searchParams.get("category") || null,
      q: searchParams.get("q") || null,
      sort: searchParams.get("sort") || (slug === "edibles" ? "price_desc" : "featured"),
      min: searchParams.get("min") || null,
      max: searchParams.get("max") || null,
      sale: searchParams.get("sale") || null,
    }),
    [slug, searchParams]
  );

  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === false || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next);
  };

  useEffect(() => {
    fetchCategories().then((d) => setCategories(d.items.filter((c) => c.product_count > 0))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts({
      category: params.category || undefined,
      q: params.q || undefined,
      sort: params.sort,
      min_price: params.min || undefined,
      max_price: params.max || undefined,
      in_stock: true,
      on_sale: !!params.sale,
      limit: 500,
    })
      .then((d) => {
        let items = d.items.filter((p) => p.price > 0 || p.from_price > 0);
        if (params.category === "disposables-carts" && params.sort === "featured") {
          const rank = (p) => (/boutiq/i.test(p.name) ? 0 : /aloha/i.test(p.name) ? 1 : /sticky/i.test(p.name) ? 2 : 3);
          items = [...items].sort((a, b) => rank(a) - rank(b));
        }
        setData({ ...d, items, total: items.length });
      })
      .catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }))
      .finally(() => setLoading(false));
  }, [params]);

  const activeCat = categories.find((c) => c.slug === params.category);
  const children = activeCat ? categories.filter((c) => c.parent_slug === activeCat.slug) : [];
  const parent = activeCat?.parent_slug ? categories.find((c) => c.slug === activeCat.parent_slug) : null;
  const siblings = parent ? categories.filter((c) => c.parent_slug === parent.slug) : [];
  const title = activeCat ? activeCat.name : params.sale ? "Deals" : "Shop All";

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14" data-testid="shop-page">
      <Seo
        title={`${title} | Farmacy District DC`}
        description={`Shop ${title.toLowerCase()} at Farmacy District DC — premium cannabis with fast, discreet delivery across Washington, DC. Adults 21+.`}
      />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">Farmacy District DC</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter sm:text-5xl" data-testid="shop-title">{title}</h1>
          <p className="mt-2 text-sm text-neutral-500" data-testid="shop-result-count">
            {data.total} product{data.total === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button className="flex items-center gap-2 border border-neutral-300 px-4 py-2.5 text-sm font-bold lg:hidden" data-testid="mobile-filters-button">
                <SlidersHorizontal className="h-4 w-4" /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85%] max-w-sm overflow-y-auto p-6">
              <SheetHeader className="mb-6">
                <SheetTitle className="font-display text-xl font-black">Filters</SheetTitle>
              </SheetHeader>
              <Filters categories={categories} params={params} update={update} />
            </SheetContent>
          </Sheet>
          <Select value={params.sort} onValueChange={(v) => update({ sort: v })}>
            <SelectTrigger className="w-[180px]" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-8 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="shop-category-chips">
        <button
          onClick={() => navigate("/shop/all")}
          data-testid="shop-chip-all"
          className={`flex-shrink-0 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
            !params.category ? "border-brand-ink bg-brand-ink text-white" : "border-neutral-300 hover:border-brand-ink"
          }`}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.category_id}
            onClick={() => navigate(`/category/${c.slug}`)}
            data-testid={`shop-chip-${c.slug}`}
            className={`flex-shrink-0 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              params.category === c.slug
                ? "border-brand-ink bg-brand-ink text-white"
                : "border-neutral-300 hover:border-brand-ink"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {parent && (
        <div className="mb-6" data-testid="subcategory-row">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/category/${parent.slug}`)}
              data-testid={`subcategory-all-${parent.slug}`}
              className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-white"
            >
              All {parent.name}
            </button>
            {siblings.map((c) => (
              <button
                key={c.category_id}
                onClick={() => navigate(`/category/${c.slug}`)}
                data-testid={`subcategory-${c.slug}`}
                className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-colors ${
                  c.slug === activeCat.slug
                    ? "border-brand-ink bg-brand-ink text-white"
                    : "border-neutral-300 hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                }`}
              >
                {c.name} <span className="ml-1 text-xs font-semibold opacity-60">({c.product_count})</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {children.length > 0 && (
        <div className="mb-6" data-testid="subcategory-row">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Browse {activeCat.name}</p>
          <div className="flex flex-wrap gap-2">
            {children.map((c) => (
              <button
                key={c.category_id}
                onClick={() => navigate(`/category/${c.slug}`)}
                data-testid={`subcategory-${c.slug}`}
                className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-white"
              >
                {c.name} <span className="ml-1 text-xs font-semibold opacity-60">({c.product_count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:block">
          <Filters categories={categories} params={params} update={update} />
        </aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4" data-testid="shop-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-neutral-100" />
                  <div className="mt-3 h-3 w-2/3 bg-neutral-100" />
                  <div className="mt-2 h-3 w-1/3 bg-neutral-100" />
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-16 text-center" data-testid="shop-empty">
              <p className="font-display text-xl font-bold">No products match those filters.</p>
              <p className="mt-2 text-sm text-neutral-500">Try clearing a filter or browsing the full menu.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 gap-y-10 md:grid-cols-3 md:gap-6 xl:grid-cols-4" data-testid="shop-grid">
              {data.items.map((p) => (
                <ProductCard key={p.product_id} product={p} onQuickView={setQuickView} />
              ))}
            </div>
          )}

        </div>
      </div>
      <QuickView slug={quickView} onClose={() => setQuickView(null)} />
    </main>
  );
}
